/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import type BlAlertType from "./components/alert/bl-alert";
import type BlBadgeType from "./components/badge/bl-badge";
import type BlButtonType from "./components/button/bl-button";
import type BlCheckboxGroupType from "./components/checkbox-group/bl-checkbox-group";
import type BlCheckboxType from "./components/checkbox-group/checkbox/bl-checkbox";
import type BlDialogType from "./components/dialog/bl-dialog";
import type BlDrawerType from "./components/drawer/bl-drawer";
import type BlDropdownType from "./components/dropdown/bl-dropdown";
import type BlDropdownGroupType from "./components/dropdown/group/bl-dropdown-group";
import type BlDropdownItemType from "./components/dropdown/item/bl-dropdown-item";
import type BlIconType from "./components/icon/bl-icon";
import type BlInputType from "./components/input/bl-input";
import type BlPaginationType from "./components/pagination/bl-pagination";
import type BlPopoverType from "./components/popover/bl-popover";
import type BlProgressIndicatorType from "./components/progress-indicator/bl-progress-indicator";
import type BlRadioGroupType from "./components/radio-group/bl-radio-group";
import type BlRadioType from "./components/radio-group/radio/bl-radio";
import { ISelectOption } from "./components/select/bl-select";
import type BlSelectType from "./components/select/bl-select";
import type BlSelectOptionType from "./components/select/option/bl-select-option";
import type BlSwitchType from "./components/switch/bl-switch";
import type BlTabGroupType from "./components/tab-group/bl-tab-group";
import type BlTabPanelType from "./components/tab-group/tab-panel/bl-tab-panel";
import type BlTabType from "./components/tab-group/tab/bl-tab";
import type BlTextareaType from "./components/textarea/bl-textarea";
import type BlTooltipType from "./components/tooltip/bl-tooltip";
import { type EventName, createComponent } from "@lit-labs/react";
import React from "react";

export declare type BlAlertClose = CustomEvent<boolean>;

export declare type BlButtonClick = CustomEvent<string>;

export declare type BlCheckboxGroupCheckboxGroupChange = CustomEvent<string[]>;

export declare type BlDialogDialogOpen = CustomEvent<object>;

export declare type BlDialogDialogRequestClose = CustomEvent<{
  source: "close-button" | "keyboard" | "backdrop";
}>;

export declare type BlDialogDialogClose = CustomEvent<object>;

export declare type BlDrawerDrawerOpen = CustomEvent<string>;

export declare type BlDrawerDrawerClose = CustomEvent<string>;

export declare type BlDropdownDropdownOpen = CustomEvent<string>;

export declare type BlDropdownDropdownClose = CustomEvent<string>;

export declare type BlIconLoad = CustomEvent<string>;

export declare type BlIconError = CustomEvent<string>;

export declare type BlInputChange = CustomEvent<string>;

export declare type BlInputInput = CustomEvent<string>;

export declare type BlInputInvalid = CustomEvent<ValidityState>;

export declare type BlPaginationChange = CustomEvent<{
  selectedPage: number;
  prevPage: number;
  itemsPerPage: number;
}>;

export declare type BlPopoverPopoverShow = CustomEvent<string>;

export declare type BlPopoverPopoverHide = CustomEvent<string>;

export declare type BlRadioGroupRadioChange = CustomEvent<string>;

export declare type BlSelectSelect = CustomEvent<ISelectOption[]>;

export declare type BlSwitchSwitchToggle = CustomEvent<boolean>;

export declare type BlTextareaInput = CustomEvent<string>;

export declare type BlTextareaChange = CustomEvent<string>;

export declare type BlTextareaInvalid = CustomEvent<ValidityState>;

export declare type BlTooltipTooltipShow = CustomEvent<string>;

export declare type BlTooltipTooltipHide = CustomEvent<string>;

export declare type BlCheckboxCheckboxChange = CustomEvent<boolean>;

export declare type BlCheckboxFocus = CustomEvent<string>;

export declare type BlCheckboxBlur = CustomEvent<string>;

export declare type BlDropdownItemDropdownItemClick = CustomEvent<string>;

export declare type BlRadioChecked = CustomEvent<string>;

export declare type BlRadioFocus = CustomEvent<string>;

export declare type BlRadioBlur = CustomEvent<string>;

export declare type BlSelectOptionSelectOption = CustomEvent<string | null>;

export declare type BlSelectOptionFocus = CustomEvent<string | null>;

export declare type BlSelectOptionBlur = CustomEvent<string | null>;

export declare type BlTabTabSelected = CustomEvent<string>;

export declare type BlAlert = BlAlertType;

export declare type BlBadge = BlBadgeType;

export declare type BlButton = BlButtonType;

export declare type BlCheckboxGroup = BlCheckboxGroupType;

export declare type BlDialog = BlDialogType;

export declare type BlDrawer = BlDrawerType;

export declare type BlDropdown = BlDropdownType;

export declare type BlIcon = BlIconType;

export declare type BlInput = BlInputType;

export declare type BlPagination = BlPaginationType;

export declare type BlPopover = BlPopoverType;

export declare type BlProgressIndicator = BlProgressIndicatorType;

export declare type BlRadioGroup = BlRadioGroupType;

export declare type BlSelect = BlSelectType;

export declare type BlSwitch = BlSwitchType;

export declare type BlTabGroup = BlTabGroupType;

export declare type BlTextarea = BlTextareaType;

export declare type BlTooltip = BlTooltipType;

export declare type BlCheckbox = BlCheckboxType;

export declare type BlDropdownGroup = BlDropdownGroupType;

export declare type BlDropdownItem = BlDropdownItemType;

export declare type BlRadio = BlRadioType;

export declare type BlSelectOption = BlSelectOptionType;

export declare type BlTab = BlTabType;

export declare type BlTabPanel = BlTabPanelType;

/**
 * @tag bl-alert
 * @summary Baklava Alert component
 */
export const BlAlert = React.lazy(() =>
  customElements.whenDefined("bl-alert").then(() => ({
    default: createComponent<
      BlAlertType,
      { onBlClose: EventName<BlAlertClose> }
    >({
      react: React,
      displayName: "BlAlert",
      tagName: "bl-alert",
      elementClass: customElements.get("bl-alert"),
      events: { onBlClose: "bl-close" },
    }),
  }))
);

/**
 * @tag bl-badge
 * @summary Baklava Badge component
 *
 * @cssproperty [--bl-badge-bg-color=--bl-color-primary-contrast] Sets the background color of badge
 * @cssproperty [--bl-badge-color=--bl-color-primary] Sets the color of badge
 */
export const BlBadge = React.lazy(() =>
  customElements.whenDefined("bl-badge").then(() => ({
    default: createComponent<BlBadgeType>({
      react: React,
      displayName: "BlBadge",
      tagName: "bl-badge",
      elementClass: customElements.get("bl-badge"),
      events: {},
    }),
  }))
);

/**
 * @tag bl-button
 * @summary Baklava Button component
 *
 * @cssproperty [--bl-button-display=inline-block] Sets the display property of button
 * @cssproperty [--bl-button-justify=center] Sets the justify-content property of button
 *
 */
export const BlButton = React.lazy(() =>
  customElements.whenDefined("bl-button").then(() => ({
    default: createComponent<
      BlButtonType,
      { onBlClick: EventName<BlButtonClick> }
    >({
      react: React,
      displayName: "BlButton",
      tagName: "bl-button",
      elementClass: customElements.get("bl-button"),
      events: { onBlClick: "bl-click" },
    }),
  }))
);

/**
 * @tag bl-checkbox-group
 * @summary Baklava Button component
 *
 * @cssproperty [--bl-checkbox-direction=row] Can be used for showing checkbox options as columns instead of rows. Options are `row` or `column`
 */
export const BlCheckboxGroup = React.lazy(() =>
  customElements.whenDefined("bl-checkbox-group").then(() => ({
    default: createComponent<
      BlCheckboxGroupType,
      { onBlCheckboxGroupChange: EventName<BlCheckboxGroupCheckboxGroupChange> }
    >({
      react: React,
      displayName: "BlCheckboxGroup",
      tagName: "bl-checkbox-group",
      elementClass: customElements.get("bl-checkbox-group"),
      events: { onBlCheckboxGroupChange: "bl-checkbox-group-change" },
    }),
  }))
);

/**
 * @tag bl-dialog
 * @summary Baklava Dialog component
 */
export const BlDialog = React.lazy(() =>
  customElements.whenDefined("bl-dialog").then(() => ({
    default: createComponent<
      BlDialogType,
      {
        onBlDialogOpen: EventName<BlDialogDialogOpen>;
        onBlDialogRequestClose: EventName<BlDialogDialogRequestClose>;
        onBlDialogClose: EventName<BlDialogDialogClose>;
      }
    >({
      react: React,
      displayName: "BlDialog",
      tagName: "bl-dialog",
      elementClass: customElements.get("bl-dialog"),
      events: {
        onBlDialogOpen: "bl-dialog-open",
        onBlDialogRequestClose: "bl-dialog-request-close",
        onBlDialogClose: "bl-dialog-close",
      },
    }),
  }))
);

/**
 * @tag bl-drawer
 * @summary Baklava Drawer component
 *
 * @cssproperty [--bl-drawer-animation-duration=250ms] Drawer slide in animation duration
 */
export const BlDrawer = React.lazy(() =>
  customElements.whenDefined("bl-drawer").then(() => ({
    default: createComponent<
      BlDrawerType,
      {
        onBlDrawerOpen: EventName<BlDrawerDrawerOpen>;
        onBlDrawerClose: EventName<BlDrawerDrawerClose>;
      }
    >({
      react: React,
      displayName: "BlDrawer",
      tagName: "bl-drawer",
      elementClass: customElements.get("bl-drawer"),
      events: {
        onBlDrawerOpen: "bl-drawer-open",
        onBlDrawerClose: "bl-drawer-close",
      },
    }),
  }))
);

/**
 * @tag bl-dropdown
 * @summary Baklava Dropdown component
 */
export const BlDropdown = React.lazy(() =>
  customElements.whenDefined("bl-dropdown").then(() => ({
    default: createComponent<
      BlDropdownType,
      {
        onBlDropdownOpen: EventName<BlDropdownDropdownOpen>;
        onBlDropdownClose: EventName<BlDropdownDropdownClose>;
      }
    >({
      react: React,
      displayName: "BlDropdown",
      tagName: "bl-dropdown",
      elementClass: customElements.get("bl-dropdown"),
      events: {
        onBlDropdownOpen: "bl-dropdown-open",
        onBlDropdownClose: "bl-dropdown-close",
      },
    }),
  }))
);

/**
 * @tag bl-icon
 * @summary Baklava Icon component
 *
 * @cssproperty [font-size] Setting size of icon. Default is current font size in DOM place
 * @cssproperty [color=currentColor] Setting color of icon
 */
export const BlIcon = React.lazy(() =>
  customElements.whenDefined("bl-icon").then(() => ({
    default: createComponent<
      BlIconType,
      { onBlLoad: EventName<BlIconLoad>; onBlError: EventName<BlIconError> }
    >({
      react: React,
      displayName: "BlIcon",
      tagName: "bl-icon",
      elementClass: customElements.get("bl-icon"),
      events: { onBlLoad: "bl-load", onBlError: "bl-error" },
    }),
  }))
);

/**
 * @tag bl-input
 * @summary Baklava Input component
 *
 * @cssproperty [--bl-input-padding-start] Sets the padding start
 * @cssproperty [--bl-input-padding-end] Sets the padding end
 */
export const BlInput = React.lazy(() =>
  customElements.whenDefined("bl-input").then(() => ({
    default: createComponent<
      BlInputType,
      {
        onBlChange: EventName<BlInputChange>;
        onBlInput: EventName<BlInputInput>;
        onBlInvalid: EventName<BlInputInvalid>;
      }
    >({
      react: React,
      displayName: "BlInput",
      tagName: "bl-input",
      elementClass: customElements.get("bl-input"),
      events: {
        onBlChange: "bl-change",
        onBlInput: "bl-input",
        onBlInvalid: "bl-invalid",
      },
    }),
  }))
);

/**
 * @tag bl-pagination
 * @summary Baklava Pagination component
 */
export const BlPagination = React.lazy(() =>
  customElements.whenDefined("bl-pagination").then(() => ({
    default: createComponent<
      BlPaginationType,
      { onBlChange: EventName<BlPaginationChange> }
    >({
      react: React,
      displayName: "BlPagination",
      tagName: "bl-pagination",
      elementClass: customElements.get("bl-pagination"),
      events: { onBlChange: "bl-change" },
    }),
  }))
);

/**
 * @tag bl-popover
 * @summary Baklava Popover component
 *
 * @cssproperty [--bl-popover-arrow-display=none] - Sets the display of popovers arrow. Set as `block` to make arrow visible.
 * @cssproperty [--bl-popover-background-color=--bl-color-neutral-full] - Sets the background color of popover.
 * @cssproperty [--bl-popover-border-color=--bl-color-primary-highlight] - Sets the border color of popover.
 * @cssproperty [--bl-popover-border-size=1px] - Sets the border size of popover. You can set it to `0px` to not have a border (if you use a custom background color). Always use with a length unit.
 * @cssproperty [--bl-popover-padding=--bl-size-m] - Sets the padding of popover.
 * @cssproperty [--bl-popover-border-radius=--bl-size-3xs] - Sets the border radius of popover.
 * @cssproperty [--bl-popover-max-width=100vw] - Sets the maximum width of the popover (including border and padding).
 * @cssproperty [--bl-popover-position=fixed] - Sets the position of popover. You can set it to `absolute` if parent element is a fixed positioned element like drawer or dialog.
 */
export const BlPopover = React.lazy(() =>
  customElements.whenDefined("bl-popover").then(() => ({
    default: createComponent<
      BlPopoverType,
      {
        onBlPopoverShow: EventName<BlPopoverPopoverShow>;
        onBlPopoverHide: EventName<BlPopoverPopoverHide>;
      }
    >({
      react: React,
      displayName: "BlPopover",
      tagName: "bl-popover",
      elementClass: customElements.get("bl-popover"),
      events: {
        onBlPopoverShow: "bl-popover-show",
        onBlPopoverHide: "bl-popover-hide",
      },
    }),
  }))
);

/**
 * @tag bl-progress-indicator
 * @summary Baklava Progress Indicator component
 *
 * @cssproperty [--bl-progress-indicator-transition-duration=.2s] Duration of the transition of progress bar
 * @property {max} [max=100]
 * @property {number} [value=0]
 */
export const BlProgressIndicator = React.lazy(() =>
  customElements.whenDefined("bl-progress-indicator").then(() => ({
    default: createComponent<BlProgressIndicatorType>({
      react: React,
      displayName: "BlProgressIndicator",
      tagName: "bl-progress-indicator",
      elementClass: customElements.get("bl-progress-indicator"),
      events: {},
    }),
  }))
);

/**
 * @tag bl-radio-group
 * @summary Baklava Button component
 *
 * @cssproperty [--bl-radio-direction=row] Can be used for showing radio options as columns instead of rows. Options are `row` or `column`
 * @cssproperty [--bl-radio-group-cross-axis-item-alignment=normal] Can be used for aligning radio items on cross axis. Acts same with align-item
 * @cssproperty [--bl-radio-group-cross-axis-content-alignment=normal] Can be used for aligning radio group content on cross axis. Acts same with align-content
 * @cssproperty [--bl-radio-group-main-axis-content-alignment=normal] Can be used for aligning radio group content on main axis. Acts same with justify-content
 */
export const BlRadioGroup = React.lazy(() =>
  customElements.whenDefined("bl-radio-group").then(() => ({
    default: createComponent<
      BlRadioGroupType,
      { onBlRadioChange: EventName<BlRadioGroupRadioChange> }
    >({
      react: React,
      displayName: "BlRadioGroup",
      tagName: "bl-radio-group",
      elementClass: customElements.get("bl-radio-group"),
      events: { onBlRadioChange: "bl-radio-change" },
    }),
  }))
);

/**
 * @tag bl-select
 * @summary Baklava Select component
 *
 * @cssproperty [--bl-popover-position=fixed] Sets the positioning strategy of select popover. You can set it as `absolute` if you need to show popover relative to its trigger element.
 */
export const BlSelect = React.lazy(() =>
  customElements.whenDefined("bl-select").then(() => ({
    default: createComponent<
      BlSelectType,
      { onBlSelect: EventName<BlSelectSelect> }
    >({
      react: React,
      displayName: "BlSelect",
      tagName: "bl-select",
      elementClass: customElements.get("bl-select"),
      events: { onBlSelect: "bl-select" },
    }),
  }))
);

/**
 * @tag bl-switch
 * @summary Baklava Switch component
 *
 * @cssproperty [--bl-switch-color-on=--bl-color-primary] Set the checked color
 * @cssproperty [--bl-switch-color-off=--bl-color-neutral-lighter] Set the unchecked color
 * @cssproperty [--bl-switch-animation-duration=300ms] Set the animation duration of switch toggle
 */
export const BlSwitch = React.lazy(() =>
  customElements.whenDefined("bl-switch").then(() => ({
    default: createComponent<
      BlSwitchType,
      { onBlSwitchToggle: EventName<BlSwitchSwitchToggle> }
    >({
      react: React,
      displayName: "BlSwitch",
      tagName: "bl-switch",
      elementClass: customElements.get("bl-switch"),
      events: { onBlSwitchToggle: "bl-switch-toggle" },
    }),
  }))
);

/**
 * @tag bl-tab-group
 * @summary Baklava Tab group component
 */
export const BlTabGroup = React.lazy(() =>
  customElements.whenDefined("bl-tab-group").then(() => ({
    default: createComponent<BlTabGroupType>({
      react: React,
      displayName: "BlTabGroup",
      tagName: "bl-tab-group",
      elementClass: customElements.get("bl-tab-group"),
      events: {},
    }),
  }))
);

/**
 * @tag bl-textarea
 * @summary Baklava Textarea component
 */
export const BlTextarea = React.lazy(() =>
  customElements.whenDefined("bl-textarea").then(() => ({
    default: createComponent<
      BlTextareaType,
      {
        onBlInput: EventName<BlTextareaInput>;
        onBlChange: EventName<BlTextareaChange>;
        onBlInvalid: EventName<BlTextareaInvalid>;
      }
    >({
      react: React,
      displayName: "BlTextarea",
      tagName: "bl-textarea",
      elementClass: customElements.get("bl-textarea"),
      events: {
        onBlInput: "bl-input",
        onBlChange: "bl-change",
        onBlInvalid: "bl-invalid",
      },
    }),
  }))
);

/**
 * @tag bl-tooltip
 * @summary Baklava Tooltip component
 * @dependency bl-popover
 *
 * @cssproperty [--bl-tooltip-trigger-display=inline-flex] Set the display of the tooltip trigger.
 */
export const BlTooltip = React.lazy(() =>
  customElements.whenDefined("bl-tooltip").then(() => ({
    default: createComponent<
      BlTooltipType,
      {
        onBlTooltipShow: EventName<BlTooltipTooltipShow>;
        onBlTooltipHide: EventName<BlTooltipTooltipHide>;
      }
    >({
      react: React,
      displayName: "BlTooltip",
      tagName: "bl-tooltip",
      elementClass: customElements.get("bl-tooltip"),
      events: {
        onBlTooltipShow: "bl-tooltip-show",
        onBlTooltipHide: "bl-tooltip-hide",
      },
    }),
  }))
);

/**
 * @tag bl-checkbox
 * @summary Baklava Checkbox component
 */
export const BlCheckbox = React.lazy(() =>
  customElements.whenDefined("bl-checkbox").then(() => ({
    default: createComponent<
      BlCheckboxType,
      {
        onBlCheckboxChange: EventName<BlCheckboxCheckboxChange>;
        onBlFocus: EventName<BlCheckboxFocus>;
        onBlBlur: EventName<BlCheckboxBlur>;
      }
    >({
      react: React,
      displayName: "BlCheckbox",
      tagName: "bl-checkbox",
      elementClass: customElements.get("bl-checkbox"),
      events: {
        onBlCheckboxChange: "bl-checkbox-change",
        onBlFocus: "bl-focus",
        onBlBlur: "bl-blur",
      },
    }),
  }))
);

/**
 * @tag bl-dropdown-group
 * @summary Baklava Dropdown Group component
 */
export const BlDropdownGroup = React.lazy(() =>
  customElements.whenDefined("bl-dropdown-group").then(() => ({
    default: createComponent<BlDropdownGroupType>({
      react: React,
      displayName: "BlDropdownGroup",
      tagName: "bl-dropdown-group",
      elementClass: customElements.get("bl-dropdown-group"),
      events: {},
    }),
  }))
);

/**
 * @tag bl-dropdown-item
 * @summary Baklava Dropdown Item component
 */
export const BlDropdownItem = React.lazy(() =>
  customElements.whenDefined("bl-dropdown-item").then(() => ({
    default: createComponent<
      BlDropdownItemType,
      { onBlDropdownItemClick: EventName<BlDropdownItemDropdownItemClick> }
    >({
      react: React,
      displayName: "BlDropdownItem",
      tagName: "bl-dropdown-item",
      elementClass: customElements.get("bl-dropdown-item"),
      events: { onBlDropdownItemClick: "bl-dropdown-item-click" },
    }),
  }))
);

/**
 * @tag bl-radio
 * @summary Baklava Radio Option component
 *
 * @cssprop [--bl-radio-align-items=center] Align items of radio option
 */
export const BlRadio = React.lazy(() =>
  customElements.whenDefined("bl-radio").then(() => ({
    default: createComponent<
      BlRadioType,
      {
        onBlChecked: EventName<BlRadioChecked>;
        onBlFocus: EventName<BlRadioFocus>;
        onBlBlur: EventName<BlRadioBlur>;
      }
    >({
      react: React,
      displayName: "BlRadio",
      tagName: "bl-radio",
      elementClass: customElements.get("bl-radio"),
      events: {
        onBlChecked: "bl-checked",
        onBlFocus: "bl-focus",
        onBlBlur: "bl-blur",
      },
    }),
  }))
);

export const BlSelectOption = React.lazy(() =>
  customElements.whenDefined("bl-select-option").then(() => ({
    default: createComponent<
      BlSelectOptionType,
      {
        onBlSelectOption: EventName<BlSelectOptionSelectOption>;
        onBlFocus: EventName<BlSelectOptionFocus>;
        onBlBlur: EventName<BlSelectOptionBlur>;
      }
    >({
      react: React,
      displayName: "BlSelectOption",
      tagName: "bl-select-option",
      elementClass: customElements.get("bl-select-option"),
      events: {
        onBlSelectOption: "bl-select-option",
        onBlFocus: "bl-focus",
        onBlBlur: "bl-blur",
      },
    }),
  }))
);

/**
 * @tag bl-tab
 * @summary Baklava Tab component
 */
export const BlTab = React.lazy(() =>
  customElements.whenDefined("bl-tab").then(() => ({
    default: createComponent<
      BlTabType,
      { onBlTabSelected: EventName<BlTabTabSelected> }
    >({
      react: React,
      displayName: "BlTab",
      tagName: "bl-tab",
      elementClass: customElements.get("bl-tab"),
      events: { onBlTabSelected: "bl-tab-selected" },
    }),
  }))
);

/**
 * @tag bl-tab-panel
 * @summary Baklava Tab panel component
 */
export const BlTabPanel = React.lazy(() =>
  customElements.whenDefined("bl-tab-panel").then(() => ({
    default: createComponent<BlTabPanelType>({
      react: React,
      displayName: "BlTabPanel",
      tagName: "bl-tab-panel",
      elementClass: customElements.get("bl-tab-panel"),
      events: {},
    }),
  }))
);
